import React, { useMemo, useState } from "react";
import { Boxes, CalendarRange, Factory, Shapes } from "lucide-react";
import AnalyticsCard from "../component/AnalyticsCard";
import ActiveHistogram from "../component/ActiveHistogram";
import ActiveAreaChart from "../component/ActiveAreaChart";
import DateBrush from "../component/DateBrush";
import AnalyticsRankingChart from "../component/AnalyticsRankingChart";
import ComponentTreemap from "../component/ComponentTypeTreemap";
import styles from "./AnalyticsPage.module.css";

export default function AnalyticsPage({ reportData, reportLoading }) {
    const dates = useMemo(() => {
        return Array.from(
            new Set((reportData || []).map(d => d.date))
        ).sort();
    }, [reportData]);

    const [range, setRange] = useState(() => ({
        from: dates[0],
        to: dates[dates.length - 1]
    }));

    const safeRange = useMemo(() => {
        if (!dates.length) return null;
        return {
            from: range?.from || dates[0],
            to: range?.to || dates[dates.length - 1]
        };
    }, [dates, range]);

    const filteredData = useMemo(() => {
        if (!safeRange) return [];
        return reportData.filter(d =>
            d.date >= safeRange.from &&
            d.date <= safeRange.to
        );
    }, [reportData, safeRange]);

    const summary = useMemo(() => {
        const total = filteredData.reduce((sum, item) => sum + (Number(item.cnt) || 0), 0);
        const unique = field => new Set(filteredData.map(item => item[field]).filter(Boolean)).size;
        const manufacturerTotals = filteredData.reduce((acc, item) => {
            const name = item.manufacturer || "Не указан";
            acc[name] = (acc[name] || 0) + (Number(item.cnt) || 0);
            return acc;
        }, {});
        const topManufacturer = Object.entries(manufacturerTotals).sort((a, b) => b[1] - a[1])[0];

        return {
            total,
            categories: unique("category"),
            types: unique("type"),
            manufacturers: unique("manufacturer"),
            topManufacturer: topManufacturer?.[0] || "—"
        };
    }, [filteredData]);

    if (reportLoading) return <div className={styles.state}>Загрузка аналитики…</div>;
    if (!reportData || reportData.length === 0) return <div className={styles.state}>Нет данных для аналитики</div>;

    return (
        <div className={styles.page}>
            <div className={styles.heading}>
                <div>
                    <h2>Аналитика компонентов</h2>
                    <p>Сводка состава, динамики и производителей за выбранный период.</p>
                </div>
                <span className={styles.periodBadge}><CalendarRange size={16} />{safeRange.from} — {safeRange.to}</span>
            </div>

            <div className={styles.metrics}>
                <Metric icon={<Boxes size={21} />} label="Всего компонентов" value={formatNumber(summary.total)} note="Суммарное количество за период" />
                <Metric icon={<Shapes size={21} />} label="Типов компонентов" value={formatNumber(summary.types)} note={`${formatNumber(summary.categories)} категорий`} />
                <Metric icon={<Factory size={21} />} label="Производителей" value={formatNumber(summary.manufacturers)} note={`Лидер: ${summary.topManufacturer}`} />
            </div>

            <div className={styles.periodPanel}>
                <div className={styles.periodHeader}>
                    <span>Период отчёта</span>
                    <span className={styles.periodRange}>
                        {safeRange.from} — {safeRange.to}
                    </span>
                </div>

                <DateBrush
                    data={reportData}
                    dates={dates}
                    range={safeRange}
                    onChange={setRange}
                />
            </div>


            <div className={styles.primaryGrid}>

                <AnalyticsCard title="Структура по активности">
                    <ActiveHistogram data={filteredData} />
                </AnalyticsCard>

                <AnalyticsCard title="Динамика по времени">
                    <ActiveAreaChart data={filteredData} />
                </AnalyticsCard>
            </div>

            <div className={styles.secondaryGrid}>
                <AnalyticsCard title="Категории и типы компонентов">
                    <ComponentTreemap
                        data={filteredData}
                        groupBy="category"
                        valueBy="type"
                    />
                </AnalyticsCard>

                <AnalyticsCard title="Ведущие производители">
                    <AnalyticsRankingChart
                        data={filteredData}
                        field="manufacturer"
                        limit={10}
                    />
                </AnalyticsCard>
            </div>
        </div>
    );
}

function Metric({ icon, label, value, note }) {
    return (
        <div className={styles.metric}>
            <div className={styles.metricIcon}>{icon}</div>
            <div>
                <span>{label}</span>
                <strong>{value}</strong>
                <small>{note}</small>
            </div>
        </div>
    );
}

const formatNumber = value => new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value || 0);
